// ระบบทำความสะอาดรูปภาพ
// ลบรูปภาพที่เริ่มพัฒนาหรือไม่ต้องการออกจากระบบ

import { doc, getDoc, updateDoc, arrayRemove } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';

/**
 * ลบรูปภาพทั้งหมดของน้องคนหนึ่ง
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<boolean>}
 */
export const deleteAllGirlImages = async (girlId) => {
  try {
    console.log(`🧹 เริ่มลบรูปภาพทั้งหมดของน้อง ${girlId}`);
    
    // ดึงข้อมูลน้องจาก Firestore
    const girlRef = doc(db, 'girls', girlId);
    const girlSnap = await getDoc(girlRef);
    
    if (!girlSnap.exists()) {
      console.log(`❌ ไม่พบข้อมูลน้อง ${girlId}`);
      return false;
    }
    
    const girlData = girlSnap.data();
    const allImages = [
      ...(girlData.images || []),
      ...(girlData.pendingImages || []),
      ...(girlData.rejectedImages || [])
    ];
    
    console.log(`📊 พบรูปภาพทั้งหมด ${allImages.length} รูป`);
    
    // ลบรูปภาพจาก Firebase Storage
    const deletePromises = allImages.map(async (image) => {
      try {
        if (image.url) {
          // แปลง URL เป็น Storage Reference
          const imageRef = ref(storage, image.url);
          await deleteObject(imageRef);
          console.log(`✅ ลบรูปภาพจาก Storage: ${image.url}`);
        }
      } catch (error) {
        console.log(`⚠️ ไม่สามารถลบรูปภาพจาก Storage: ${image.url}`, error.message);
      }
    });
    
    await Promise.all(deletePromises);
    
    // ลบข้อมูลรูปภาพจาก Firestore
    await updateDoc(girlRef, {
      images: [],
      pendingImages: [],
      rejectedImages: [],
      lastUpdated: new Date().toISOString()
    });
    
    console.log(`✅ ลบรูปภาพทั้งหมดของน้อง ${girlId} สำเร็จ`);
    return true;
    
  } catch (error) {
    console.error(`❌ เกิดข้อผิดพลาดในการลบรูปภาพของน้อง ${girlId}:`, error);
    return false;
  }
};

/**
 * ลบรูปภาพเฉพาะที่ระบุ
 * @param {string} girlId - ID ของน้อง
 * @param {string} imageUrl - URL ของรูปภาพที่ต้องการลบ
 * @returns {Promise<boolean>}
 */
export const deleteSpecificImage = async (girlId, imageUrl) => {
  try {
    console.log(`🧹 เริ่มลบรูปภาพเฉพาะ: ${imageUrl}`);
    
    // ดึงข้อมูลน้องจาก Firestore
    const girlRef = doc(db, 'girls', girlId);
    const girlSnap = await getDoc(girlRef);
    
    if (!girlSnap.exists()) {
      console.log(`❌ ไม่พบข้อมูลน้อง ${girlId}`);
      return false;
    }
    
    const girlData = girlSnap.data();
    
    // หารูปภาพที่ต้องการลบ
    let imageToDelete = null;
    let imageArray = null;
    let arrayField = null;
    
    // ตรวจสอบใน images
    if (girlData.images) {
      imageToDelete = girlData.images.find(img => img.url === imageUrl);
      if (imageToDelete) {
        imageArray = girlData.images;
        arrayField = 'images';
      }
    }
    
    // ตรวจสอบใน pendingImages
    if (!imageToDelete && girlData.pendingImages) {
      imageToDelete = girlData.pendingImages.find(img => img.url === imageUrl);
      if (imageToDelete) {
        imageArray = girlData.pendingImages;
        arrayField = 'pendingImages';
      }
    }
    
    // ตรวจสอบใน rejectedImages
    if (!imageToDelete && girlData.rejectedImages) {
      imageToDelete = girlData.rejectedImages.find(img => img.url === imageUrl);
      if (imageToDelete) {
        imageArray = girlData.rejectedImages;
        arrayField = 'rejectedImages';
      }
    }
    
    if (!imageToDelete) {
      console.log(`❌ ไม่พบรูปภาพที่ต้องการลบ: ${imageUrl}`);
      return false;
    }
    
    // ลบรูปภาพจาก Firebase Storage
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
      console.log(`✅ ลบรูปภาพจาก Storage: ${imageUrl}`);
    } catch (error) {
      console.log(`⚠️ ไม่สามารถลบรูปภาพจาก Storage: ${imageUrl}`, error.message);
    }
    
    // ลบข้อมูลรูปภาพจาก Firestore
    await updateDoc(girlRef, {
      [arrayField]: arrayRemove(imageToDelete),
      lastUpdated: new Date().toISOString()
    });
    
    console.log(`✅ ลบรูปภาพเฉพาะสำเร็จ: ${imageUrl}`);
    return true;
    
  } catch (error) {
    console.error(`❌ เกิดข้อผิดพลาดในการลบรูปภาพเฉพาะ:`, error);
    return false;
  }
};

/**
 * ลบรูปภาพแรกของน้อง (สำหรับการเริ่มพัฒนา)
 * @param {string} girlId - ID ของน้อง
 * @returns {Promise<boolean>}
 */
export const deleteFirstImage = async (girlId) => {
  try {
    console.log(`🧹 เริ่มลบรูปภาพแรกของน้อง ${girlId}`);
    
    // ดึงข้อมูลน้องจาก Firestore
    const girlRef = doc(db, 'girls', girlId);
    const girlSnap = await getDoc(girlRef);
    
    if (!girlSnap.exists()) {
      console.log(`❌ ไม่พบข้อมูลน้อง ${girlId}`);
      return false;
    }
    
    const girlData = girlSnap.data();
    
    // หารูปภาพแรก (จาก images array)
    if (girlData.images && girlData.images.length > 0) {
      const firstImage = girlData.images[0];
      console.log(`📷 พบรูปภาพแรก: ${firstImage.url}`);
      
      // ลบรูปภาพจาก Firebase Storage
      try {
        const imageRef = ref(storage, firstImage.url);
        await deleteObject(imageRef);
        console.log(`✅ ลบรูปภาพแรกจาก Storage: ${firstImage.url}`);
      } catch (error) {
        console.log(`⚠️ ไม่สามารถลบรูปภาพแรกจาก Storage: ${firstImage.url}`, error.message);
      }
      
      // ลบข้อมูลรูปภาพจาก Firestore
      await updateDoc(girlRef, {
        images: arrayRemove(firstImage),
        lastUpdated: new Date().toISOString()
      });
      
      console.log(`✅ ลบรูปภาพแรกของน้อง ${girlId} สำเร็จ`);
      return true;
    } else {
      console.log(`❌ ไม่พบรูปภาพแรกของน้อง ${girlId}`);
      return false;
    }
    
  } catch (error) {
    console.error(`❌ เกิดข้อผิดพลาดในการลบรูปภาพแรกของน้อง ${girlId}:`, error);
    return false;
  }
};









